// @flow
//#region imports
import React, { PureComponent, type Node } from "react";
import { History } from "history";
import TogglesHandler from "containers/TogglesHandler";
import "./index.css";
import { connect, Dispatch } from "react-redux";
import { compose } from "redux";
import MainLayout from "components/MainLayout";
import ArchiveTargetListContainer from "containers/ArchiveTargetList";
import FormContainer from "containers/Form";
import UploadSelection from "components/UploadSelection";
import {
    setCurrentItemAction,
    backupCurrentItemAction,
    restoreCurrentItemAction,
} from "data/actions";
import {
    type ToggleDTO,
    type ToggleItemDTO,
    NewFormType,
    UploadMode,
} from "data/types";
import * as s from "data/reducers/selectors";
import { getDefaultsConfig } from "data/constants";
import SplitPanes from "containers/SplitPanes";
import loc from "i18next";
// import FloatingBar from "components/FloatingBar";
import uppy, { uppyHasPendingUploadsSelector, type uppyFile } from "data/uppy";
import isEmpty from "lodash/isEmpty";
import { replaceFileExtension } from "data/utils";
import produce from "immer";
import { setUppyRestrictions } from "components/uppy-restrictions";
import Pane from "components/SplitPanes/Pane";
//#endregion

export const viewName = "upload";

//#region flow types
type Props = {
    /** react-router History */
    history: History,
    /** whether shown as Modal or full View page */
    asModal?: boolean,
    /** React Node to show before toolbar */
    topRowBefore?: Node,
    /** React Node to show after toolbar */
    topRowEnd?: Node,
    /** callback when closing Modal dialog */
    onClose?: () => void,
    // from TogglesHandler
    /** TogglesHandler callback to set toggles */
    setToggles: Function,
    /** TogglesHandler this view's toggles definitions */
    toggles: ?ToggleDTO,
    // from redux
    /** current itemUri (or if {@link asModal} = true) */
    itemUri: string,
    /** current formatId */
    formatId: number,
    /** whether archive form should be displayed */
    noItemUri: boolean,
    /** current viewName */
    viewName: string,
    /** Redux dispatch */
    dispatch: Dispatch,
    /** which toggles are currently selected */
    selectedToggles: Array<string>,
    /** whether there are any pending uploads */
    uppyHasPendingUploads: boolean,
    /** SplitPanes callback to set split sizes */
    setSplitSizes: Function,
    /** Redux formDefaults */
    defaults: ?Object,
};

type State = {
    itemUri: ?string,
    isFormButtonEnabled: boolean,
};
//#endregion

export class Upload extends PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            itemUri: props.itemUri,
            isFormButtonEnabled: true,
        };
        this._updateUppyOptions(props.itemUri, props.formatId);
    }

    componentDidMount() {
        const { dispatch, itemUri, viewName } = this.props;
        dispatch(backupCurrentItemAction());
        dispatch(setCurrentItemAction({ name: viewName, itemUri }));
        this._updateFormToggle(this.props.uppyHasPendingUploads);
        this._maybeResetArchiveTarget(this.props.uppyHasPendingUploads);
    }

    componentWillUnmount() {
        const { dispatch } = this.props;
        dispatch(restoreCurrentItemAction());
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { dispatch, viewName, itemUri, uppyHasPendingUploads, formatId } =
            this.props;
        /* istanbul ignore else */
        if (prevProps.itemUri !== itemUri) {
            this.setState({ itemUri });
            // this.setState({ isFlatAU: undefined, formInEditMode: false });
            dispatch(
                setCurrentItemAction({
                    name: viewName,
                    itemUri,
                })
            );
            this._updateUppyOptions(itemUri, formatId);
        }
        /* istanbul ignore else */
        if (prevState.itemUri !== this.state.itemUri) {
            this._updateUppyOptions(this.state.itemUri, formatId);
        }
        /* istanbul ignore else */
        if (prevProps.uppyHasPendingUploads !== uppyHasPendingUploads) {
            // reset currently shown breadcrumb
            if (uppyHasPendingUploads === false) {
                this.setState({ itemUri });
                this._updateUppyOptions(itemUri, formatId);
            }
            this._updateFormToggle(uppyHasPendingUploads);
            this._maybeResetArchiveTarget(uppyHasPendingUploads);
        }
    }

    _updateUppyOptions = (itemUri: string, formatId: ?number) => {
        const defaults = getDefaultsConfig(
            itemUri || "pam-item://",
            formatId || -1
        );
        /* istanbul ignore else */
        if (defaults) setUppyRestrictions(uppy, defaults.upload);
    };

    _updateFormToggle = (uppyHasPendingUploads: boolean) => {
        /* istanbul ignore if */
        if (this.props.toggles == null) return;
        // form toggles visibility
        const toggles = this.props.toggles.toggles.map(
            (toggle: ToggleItemDTO) => {
                toggle.isDisabled =
                    toggle.value === "form" && !uppyHasPendingUploads;
                toggle.isSelected =
                    toggle.value === "files" ||
                    (toggle.value === "form" && uppyHasPendingUploads);
                return toggle;
            }
        );
        /* istanbul ignore else */
        if (toggles != null) {
            this.props.setToggles({
                name: viewName,
                isMultiple: true,
                toggles,
            });
        }
    };

    _maybeResetArchiveTarget = (uppyHasPendingUploads: boolean) => {
        /* istanbul ignore else */
        // Reset upload if archive target can be set, and history is available
        if (
            this.props.noItemUri &&
            this.props.history &&
            !uppyHasPendingUploads
        ) {
            this.props.history.push("/upload");
        }
    };

    // _onActionShouldBeVisible = (action: { id: string }): boolean => {
    //     return true;
    // };

    // _onAction = (actionId: string): void => {
    //     const { itemUri } = this.props;

    //     switch (actionId) {
    //         /* istanbul ignore next */
    //         default:
    //             console.warn(actionId + ": " + itemUri);
    //             break;
    //     }
    // };

    /**
     * will try to get the first filename available
     * @returns {string} filename or null if nothing found
     * @memberof Upload
     */
    _getWebDavNameHint = () => {
        const files: Array<uppyFile> = uppy.getFiles();
        const firstFile = files.find((f) => f.meta && f.meta.itemUri == null);
        return firstFile ? firstFile.meta.name : null;
    };

    /**
     * will return defaultValues to pass on to form
     * @returns defaultValues or null if no filename found
     * @memberof Upload
     */
    _getDefaultValues = () => {
        const defaults = Object.assign({}, this.props.defaults);
        const WebDavName = this._getWebDavNameHint();
        if (WebDavName) defaults["$NAME$"] = WebDavName;
        if (!isEmpty(defaults)) return defaults;
        else return null;
    };

    /**
     * called when user clicks on Save in {@link FormContainer}
     * @async
     * @param {Object} model the form's values
     * @memberof Upload
     * @returns {boolean} true if success, false otherwise
     */
    _onSaveNew = async (
        model: Object,
        keepOriginalFilename: boolean = false
    ): Promise<boolean> => {
        const files: Array<uppyFile> = uppy.getFiles();
        if (files.length === 0) {
            // alert("No Files to archive!");
            return;
        }
        this.setState({ isFormButtonEnabled: false });

        files.forEach((file) => {
            // has itemUri set (from previous failure?)
            if (file.meta.itemUri == null) {
                // #46997 let's fix the file extension
                // HACK
                const newModel = produce(model, (draft) => {
                    draft.ip["$NAME$"] = replaceFileExtension(
                        model.ip["$NAME$"], // first filename
                        file.extension // current real extension
                    );
                });

                // attach classification form (ip/ea) to each file's metadata (managed server-side by TusMiddleware.cs)
                uppy.setFileMeta(file.id, {
                    itemUri: this.state.itemUri,
                    mode: UploadMode.CreateDocument,
                    model: JSON.stringify(newModel),
                    keepOriginalFilename,
                });
            }
        });

        try {
            const result = await uppy.upload();
            this.setState({ isFormButtonEnabled: true });
            if (result && result.failed && result.failed.length === 0) {
                // all success -> reset uppy, close and refresh
                uppy.reset();
                /* istanbul ignore else */
                if (typeof this._onClose === "function") this._onClose();
                return true;
            }
        } catch (err) {
            /* istanbul ignore next */
            console.warn("Upload returned error: ", err);
        }
        this.setState({ isFormButtonEnabled: true });
        return false;
    };

    /**
     * called when user clicks Cancel in {@link FormContainer}
     * @memberof Upload
     */
    _onClose = () => {
        //#59285 Remove file from queue on Cancel
        uppy.reset();
        if (typeof this.props.onClose === "function") {
            this.props.onClose();
        } else {
            this.setState({ itemUri: null, formatId: -1 });
        }
    };

    render() {
        const { noItemUri, toggles, viewName, uppyHasPendingUploads } =
            this.props;
        const { itemUri } = this.state;
        return (
            <MainLayout
                name={viewName}
                toggles={toggles}
                itemUri={itemUri || "upload"}
                // onActionShouldBeVisible={this._onActionShouldBeVisible}
                // onAction={
                //     /* istanbul ignore next */
                //     window.CONFIG.general.secure_params
                //         ? undefined
                //         : this._onAction
                // }
                breadcrumbReadonly={true}
                // topRowBefore={this.props.topRowBefore}
                topRowEnd={this.props.topRowEnd}
            >
                {/* {this._renderFloatingBar()} */}
                <Pane id="docFilesPanel" className="f-split" dataTest="docFilesPanel">
                    <UploadSelection uppy={uppy} />
                </Pane>
                <Pane id="docformPanel" className="f-split" dataTest="docFormPanel">
                    {uppyHasPendingUploads && noItemUri && itemUri == null && (
                        <ArchiveTargetListContainer
                            show={noItemUri && itemUri == null}
                            onTargetItemUri={
                                /*istanbul ignore next*/ (itemUri) =>
                                    this.setState({ itemUri })
                            }
                        />
                    )}
                    {uppyHasPendingUploads && itemUri && (
                        <FormContainer
                            itemUri={itemUri}
                            viewName={viewName}
                            isEditMode={true}
                            onCancel={this._onClose}
                            onSaveNew={this._onSaveNew}
                            saveLabel={loc.t("common:sidebar.upload")}
                            savingLabel={loc.t("upload:uploading")}
                            isFormButtonEnabled={this.state.isFormButtonEnabled}
                            defaultValues={this._getDefaultValues()}
                            newFormType={NewFormType.Document}
                            onSaveReturnAllFields={true}
                        />
                    )}
                </Pane>
            </MainLayout>
        );
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => {
    return {
        noItemUri: ownProps.onClose == null, //whether archive form should be displayed
        itemUri:
            ownProps.onClose == null
                ? null
                : ownProps.itemUri || s.itemUriSelector(ownProps),
        formatId: s.breadcrumbFormatIdSelector(state, viewName),
        uppyHasPendingUploads: uppyHasPendingUploadsSelector(state),
        defaults: s.formDefaultsSelector(state),
        //onWindows: bowser.windows,
        viewName,
        ...ownProps,
    };
};

export default compose(
    connect(mapStateToProps),
    TogglesHandler({
        viewName,
    }),
    SplitPanes({
        viewName,
        paneIds: ["#docFilesPanel", "#docformPanel"],
        toggleIds: ["files", "form"],
        direction: "horizontal",
    })
)(Upload);
